import {autocompleteChangeEvent, SetState} from "../../util/FormHandlers";
import React, {useMemo} from "react";
import {Autocomplete, Box, Stack, TextField, Tooltip, Typography} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import cronstrue from 'cronstrue';

type CronOption = { pattern: string, description: string };

const quarzSamples: CronOption[] = [
    {pattern: '0 0 9 ? * MON-FRI', description: 'Every weekday at 9 AM'},
    {pattern: '0 15 10 * * ?', description: 'Every day at 10:15 AM'},
    {pattern: '0 0 12 ? * SUN', description: 'Every Sunday at 12 PM'},
    {pattern: '0 0/30 8-10 * * *', description: 'Every 30 mins from 8 AM to 10 AM'},
    {pattern: '0 0/15 * * * ?', description: 'Every 15 minutes'},
    {pattern: '0 0 0 1 * ?', description: 'First day of every month at midnight'},
    {pattern: '0 0 14 * * MON', description: 'Every Monday at 2 PM'},
    {pattern: '0 0 12 L * ?', description: 'Last day of every month at noon'},
    {pattern: '0 0 10 ? * 2#1', description: 'First Monday of every month at 10 AM'},
    {pattern: '0 0 6,18 * * ?', description: 'Every day at 6 AM and 6 PM'},
    {pattern: '0 0 0 ? * SAT,SUN', description: 'Every weekend at midnight'},
    {pattern: '0 30 9-17 * * MON-FRI', description: 'Every 30 mins past the hour, 9 AM-5 PM, weekdays'},
    {pattern: '0 0 */4 * * ?', description: 'Every 4 hours'},
    {pattern: '0 0 9 15 * ?', description: '15th day of every month at 9 AM'},
    {pattern: '0 0 22 ? * 1-5', description: 'Weekdays at 10 PM'},
];

function QuartzCronHelper({description, error, onClick}: {
    description?: string,
    error?: string,
    onClick: SetState<string>,
}) {
    const options = useMemo(
        () => quarzSamples.map(sample => ({
            ...sample,
            onClick: () => onClick(sample.pattern),
        })),
        [onClick]
    );

    return (
        <Tooltip
            title={
                <Box sx={{p: 1}}>
                    <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>
                        Quartz CRON Format
                    </Typography>
                    <Typography variant="body2" sx={{fontFamily: 'monospace', mb: 1}}>
                        Seconds Minutes Hours Day-of-Month Month Day-of-Week [Year]
                    </Typography>
                    <Typography variant="subtitle2" sx={{mb: 1}}>
                        Examples:
                    </Typography>
                    <ul style={{margin: 0, paddingLeft: '1.2rem'}}>
                        {options.map(sample => (
                            <li key={sample.pattern}>
                                <Typography variant="body2">
                                    <code style={{
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        padding: '0 2px',
                                        cursor: 'pointer'
                                    }} onClick={sample.onClick}>
                                        {sample.pattern}
                                    </code> — {sample.description}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                </Box>
            }
            slotProps={{
                tooltip: {
                    sx: {
                        maxWidth: 600
                    }
                }
            }}
        >
            <Stack direction="row" gap={.5} component="span" alignItems="center">
                <Typography variant="caption" color={description ? 'success.main' : error ? 'error.main' : 'text.secondary'}>
                    {description || error || 'Quarz CRON format'}
                </Typography>
                <HelpOutlineIcon sx={{fontSize: '1rem'}}/>
            </Stack>
        </Tooltip>
    );
}


export function cronDescription(cron: string): {type: 'success', description: string} | {type: 'error', error: string} {
    try {
        return cron ? {
            type: 'success',
            description: cronstrue.toString(cron, {
                throwExceptionOnParseError: true,
                use24HourTimeFormat: true,
            }),
        } : {
            type: 'success',
            description: ''
        };
    } catch (e) {
        return {
            type: 'error',
            error: typeof e === 'string' ? e : e instanceof Error ? e.message : `Cannot parse ${cron}`,
        };
    }
}

function getPattern(value: CronOption | string | null): string {
    return !value
        ? ''
        : typeof value === 'string'
            ? value
            : value.pattern;
}

export function CronField({value, setValue, ...props}: {
    value: string,
    setValue: SetState<string>,
} & TextFieldProps) {

    const cron = useMemo(() => {
        const description = cronDescription(value);
        return {
            pattern: value,
            description: description.type === 'success' ? description.description : '',
            error: description.type === 'error' ? description.error : '',
        };
    }, [value]);

    const handleOnChange = useMemo(
        () => autocompleteChangeEvent((newValue: CronOption | string) => {
            setValue(getPattern(newValue));
        }),
        [setValue],
    );

    return (
        <Autocomplete<CronOption, false, true, true>
            freeSolo
            options={quarzSamples}
            value={cron}
            onChange={handleOnChange}
            onInputChange={handleOnChange}
            getOptionKey={getPattern}
            getOptionLabel={getPattern}
            isOptionEqualToValue={(option, value) => option.pattern === value.pattern}
            renderOption={(props, option) => {
                const {key, ...otherProps} = props as any;
                return (
                    <li key={key} {...otherProps}>
                        <Stack>
                            <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                {option.pattern}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {option.description}
                            </Typography>
                        </Stack>
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...props}
                    // placeholder="0 0 9 ? * MON-FRI"
                    helperText={
                        <QuartzCronHelper description={cron.description} error={cron.error} onClick={setValue}/>
                    }
                />
            )}
        />
    );
}
